import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { resolveTranscriptSpeakerDisplayLabel as resolveTranscriptSpeakerPresentationLabel } from "@/modules/audio-recording/transcript-speaker-identity";
import { resolveTranscriptSpeakerDisplayLabel as resolveStructuredTranscriptSpeakerDisplayLabel } from "@/modules/audio-recording/transcript-speaker-labels";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";
import type {
  TranscriptSpeakerLabelKind,
  TranscriptSpeakerParticipantMetadata,
  TranscriptSpeakerVariant,
} from "@/modules/audio-recording/transcript-types";
import { toastModule } from "@/modules/toast";
import { AudioRecordingObservable } from "@/store/audio-recordings";

export const copyRecordingTranscript = async ({
  recording,
}: {
  recording: AudioRecordingObservable | null;
}) => {
  if (!recording || !recording.transcriptTurns.length) return;

  const transcript = recording.transcriptTurns
    .map(turn => ({
      label: resolveCopyTranscriptSpeakerLabel({
        speakerVariant: turn.content.speakerVariant,
        channel: turn.channel,
        speakerParticipant: turn.content.speakerParticipant,
        speakerId: turn.content.speakerId,
      }),
      text: (turn.content.words ?? []).map(word => word.text).join(" "),
    }))
    .filter(turn => turn.text.trim())
    .map(turn => `[${turn.label}] ${turn.text}`)
    .join("\n\n");

  await copyTextToClipboard({ text: transcript });

  toastModule.triggerToast({ content: "Transcript copied to clipboard" });
};

export const copyNoteTranscript = async ({
  artifactInfo,
}: {
  artifactInfo: NoteSourceArtifactInfo;
}) => {
  if (artifactInfo?.kind !== "AUDIO" || !artifactInfo.data) return;

  const transcriptArray = artifactInfo.data.transcript_array as TranscriptArrayItem[] | undefined;
  const transcript =
    transcriptArray
      ?.filter(t => t.transcript?.trim())
      ?.map(
        t =>
          `[${resolveCopyTranscriptSpeakerLabel({
            speakerVariant: t.speaker_variant,
            channel: t.channel,
            speakerParticipant: t.speaker_participant,
            speakerId: t.speaker_id,
            speakerLabel: t.speaker_label,
            speakerLabelKind: t.speaker_label_kind,
          })}] ${t.transcript}`
      )
      .join("\n\n") || artifactInfo.data.transcript_text;
  if (!transcript) return;

  await copyTextToClipboard({ text: transcript });

  toastModule.triggerToast({ content: "Transcript copied to clipboard" });
};

function resolveCopyTranscriptSpeakerLabel({
  speakerVariant,
  channel,
  speakerParticipant,
  speakerId,
  speakerLabel,
  speakerLabelKind,
}: {
  speakerVariant?: TranscriptSpeakerVariant | null;
  channel?: number | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerId?: string | null;
  speakerLabel?: number | string | null;
  speakerLabelKind?: TranscriptSpeakerLabelKind | null;
}): string {
  const structuredLabel = resolveStructuredTranscriptSpeakerDisplayLabel({
    channel,
    speakerParticipant,
    speakerLabel,
    speakerLabelKind,
  });

  if (structuredLabel.source !== "channel") {
    return structuredLabel.label;
  }

  return resolveTranscriptSpeakerPresentationLabel({
    speakerVariant,
    channel,
    speakerParticipant,
    speakerId,
  });
}
